import { fetcher } from '@commenda/utils';

import {
  IIndirectTaxBreakdownJobResponse,
  IIndirectTaxBreakdownTriggerParams,
  IIndirectTaxFilingV2MutationResult,
} from './types';

/**
 * POST /api/v1/new-sales-tax/corporations/:corporationId/tax-breakdowns/generate
 *
 * Full-surface breakdown trigger for the admin indirect-tax tools. Forwards to
 * sales-tax-api-2's /tax-breakdowns/trigger, which accepts exactly one filter
 * group — invoice, filing ids, filing due-date range, or transaction date range
 * — and returns a 400 naming the conflict otherwise. Only the fields the caller
 * set are sent, so group exclusivity is decided by whichever group the UI
 * filled in.
 *
 * Prefer triggerIndirectTaxFilingsV2BreakdownGeneration for the plain
 * "re-run these filings" case; this exists for the wider filter set.
 */
export async function triggerIndirectTaxBreakdowns({
  params,
  token,
}: {
  params: IIndirectTaxBreakdownTriggerParams;
  token?: string;
}): Promise<
  IIndirectTaxFilingV2MutationResult<IIndirectTaxBreakdownJobResponse>
> {
  try {
    const body: Record<string, unknown> = {};

    if (params.invoiceId) body.invoice_id = params.invoiceId;
    if (params.filingId) body.filing_id = params.filingId;
    if (params.filingIds?.length) body.filing_ids = params.filingIds;
    if (params.dueDateStart) body.due_date_start = params.dueDateStart;
    if (params.dueDateEnd) body.due_date_end = params.dueDateEnd;
    if (params.country) body.country = params.country;
    if (params.state) body.state = params.state;
    if (params.transactionStartDate) {
      body.transaction_start_date = params.transactionStartDate;
    }
    if (params.transactionEndDate) {
      body.transaction_end_date = params.transactionEndDate;
    }
    if (params.forceRegenerate) body.force_regenerate = true;

    if (Object.keys(body).length === 0) {
      return { success: false, error: 'Pick a filter before running' };
    }

    const response = await fetcher(
      `new-sales-tax/corporations/${params.corporationId}/tax-breakdowns/generate`,
      { method: 'POST', body },
      token
    );

    const json = await response.json();

    if (!response.ok) {
      const message = Array.isArray(json?.message)
        ? json.message.join(', ')
        : json?.message;
      return {
        success: false,
        error:
          typeof message === 'string' && message
            ? message
            : 'Failed to trigger tax-breakdown generation',
      };
    }

    return {
      success: true,
      data: { jobId: json?.data?.job_id, message: json?.message ?? '' },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to trigger tax-breakdown generation',
    };
  }
}
